import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FirstPageOutlinedIcon from "@mui/icons-material/FirstPageOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LanguageIconOutlined from "@mui/icons-material/LanguageOutlined";
import LastPageOutlinedIcon from "@mui/icons-material/LastPageOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
} from "@mui/material";
import React, { useContext } from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { RequireAuth, useFusionAuth } from "@fusionauth/react-sdk";
import {
  NavBarContext,
  NavBarDispatchContext,
} from "../../contexts/NavBarContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import { useFetch } from "../../services/hooks/useFetch.ts";
import theme from "../../styles/theme.ts";
import { userApiPrefix } from "../../utils/main.ts";

interface SettingsPageDefinition {
  page: string;
  primary: string;
  secondary: string;
  isAdminOnly?: boolean;
  displayCheck?: () => boolean;
}

export default function NavBar() {
  const settingsPath = "/settings";

  const { userInfo } = useFusionAuth();
  const isAdmin = userInfo?.roles?.includes("ADMIN");

  const navigate = useNavigate();
  const location = useLocation();
  const isSettingsPage = () => location.pathname === settingsPath;
  const { startLogout } = useFusionAuth();
  const {
    open,
    projectsExpanded,
    globalExpanded,
    company,
    showCompanySwitcher,
  } = useContext(NavBarContext);
  const navBarDispatch = useContext(NavBarDispatchContext);
  const { delete: clearSelectedGroup } = useFetch();

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const project = useContext(ProjectContext);

  const goProjects = () => {
    navigate("/");
    if (open) {
      navBarDispatch({ type: "close" });
    }
  };

  const goSettings = (settings: string, global: boolean) => {
    let newSearchParams;
    if (!global && projectId) {
      newSearchParams = createSearchParams({
        projectId: projectId,
        settings: settings,
      }).toString();
    } else {
      newSearchParams = createSearchParams({
        settings: settings,
      }).toString();
    }

    let previousPath: string;

    if (!window.location.pathname.includes("settings")) {
      previousPath = window.location.pathname + window.location.search;
    } else {
      previousPath = location.state?.previousPath;
    }

    navigate(
      {
        pathname: settingsPath,
        search: newSearchParams,
      },
      {
        state: {
          previousPath: previousPath,
        },
      },
    );
    if (open) {
      navBarDispatch({ type: "close" });
    }
  };

  const handleLogout = () => {
    void clearSelectedGroup({
      path: `${userApiPrefix}/user/group/clear`,
      postResponseAction: () => {
        startLogout();
      },
    });
  };

  const projectSettingPages: SettingsPageDefinition[] = [
    {
      page: "device-details",
      primary: "Device Details",
      secondary: "Manufacturer, GMDN, Description",
      displayCheck: () => !!project && project.type === 1,
    },
    {
      page: "protocol",
      primary: "Protocol",
      secondary: "Dates & Criteria",
    },
    {
      page: "patient-cohort",
      primary: "Patient Cohort",
      secondary: "Manage patient cohort",
      isAdminOnly: true,
    },
    {
      page: "project-details",
      primary: "Project Details",
      secondary: "Status, Name, Description",
    },
    {
      page: "prioritization",
      primary: "Prioritization",
      secondary: "Tools to prioritize records",
    },
    {
      page: "custom-categories",
      primary: "Custom Categories",
      secondary: "Add your own categories",
      isAdminOnly: true,
    },
    {
      page: "additional-data",
      primary: "Additional Data",
      secondary: "Project Feature Settings",
      isAdminOnly: true,
    },
    {
      page: "project-permissions",
      primary: "Project Permissions",
      secondary: "Lock/Unlock Access",
      isAdminOnly: true,
    },
    {
      page: "snapshot",
      primary: "Project Snapshots",
      secondary: "Saved Project Versions",
      isAdminOnly: true,
    },
    {
      page: "incremental",
      primary: "Incremental Settings",
      secondary: "Source, Dates",
      isAdminOnly: true,
    },
  ];

  const globalSettingPages: SettingsPageDefinition[] = [
    {
      page: "company",
      primary: "Company Settings",
      secondary: "Company Name",
    },
    {
      page: "manufacturer-settings",
      primary: "Manufacturer Settings",
      secondary: "Device Mfr. Domain, Device Mfr. Aliasing",
    },
    {
      page: "user",
      primary: "User Settings",
      secondary: "User Management",
    },
    {
      page: "flowsheet-groups",
      primary: "Flowsheet Groups",
      secondary: "Manage flowsheet data",
    },
    {
      page: "lab-groups",
      primary: "Lab Groups",
      secondary: "Manage lab data",
    },
    {
      page: "prom-groups",
      primary: "Prom Groups",
      secondary: "Manage prom data",
    },
  ];

  return (
    <>
      <Paper className="App-sideBar" elevation={0} square>
        <List className="sidebar-body">
          <div className="sidebar-logo">
            <img src={"/logo.svg"} alt="logo-icon" className="logo-icon" />
          </div>
          <Tooltip placement="right" title="All Projects">
            <ListItemButton
              className="folder-icon"
              onClick={() => goProjects()}
            >
              <FolderOutlinedIcon
                style={{ color: theme.palette.action.active }}
              />
            </ListItemButton>
          </Tooltip>
          {projectId !== null && (
            <Tooltip placement="right" title="Project Settings">
              <ListItemButton
                className="settings-icon"
                onClick={() => {
                  navBarDispatch({ type: "openProject" });
                }}
              >
                <SettingsOutlinedIcon
                  style={{ color: theme.palette.action.active }}
                />
              </ListItemButton>
            </Tooltip>
          )}
          <RequireAuth withRole="ADMIN">
            <Tooltip placement="right" title="Global Settings">
              <ListItemButton
                className="settings-icon"
                onClick={() => navBarDispatch({ type: "openGlobal" })}
              >
                <LanguageIconOutlined
                  style={{ color: theme.palette.action.active }}
                />
              </ListItemButton>
            </Tooltip>
          </RequireAuth>
        </List>
        <div className="general-drawer-footer">
          {showCompanySwitcher && company?.name && (
            <Tooltip
              placement="right"
              title={`${company?.name}. Click to switch company`}
            >
              <ListItemButton onClick={() => navigate("/company")}>
                <DomainOutlinedIcon
                  style={{ color: theme.palette.action.active }}
                />
              </ListItemButton>
            </Tooltip>
          )}
          <Tooltip placement="right" title="Logout">
            <ListItemButton onClick={handleLogout}>
              <LogoutIcon style={{ color: theme.palette.action.active }} />
            </ListItemButton>
          </Tooltip>
          <Tooltip placement="right" title="Expand">
            <ListItemButton onClick={() => navBarDispatch({ type: "open" })}>
              <LastPageOutlinedIcon
                style={{ color: theme.palette.action.active }}
              />
            </ListItemButton>
          </Tooltip>
        </div>
        <Drawer
          className="drawer"
          anchor="left"
          open={open}
          slotProps={
            isSettingsPage()
              ? {
                  backdrop: {
                    style: { opacity: 0 },
                  },
                }
              : {}
          }
          onClose={() => {
            navBarDispatch({ type: "close" });
          }}
        >
          <Box className="box" p={2} role="presentation">
            <List className="general-drawer-body-box">
              <img src={"/logo-mark.svg"} alt="logo" className="logo-full" />
              <ListItem className="project-full">
                <ListItemButton
                  className="left-align-ListItem"
                  onClick={goProjects}
                >
                  <ListItemIcon>
                    <FolderOutlinedIcon
                      style={{ color: theme.palette.action.active }}
                    />
                  </ListItemIcon>
                  <ListItemText primary="All Projects" />
                </ListItemButton>
              </ListItem>
              {projectId !== null && (
                <>
                  <ListItem className="project-full">
                    <ListItemButton
                      className="left-align-ListItem"
                      onClick={() =>
                        navBarDispatch({
                          type: projectsExpanded
                            ? "closeProject"
                            : "openProject",
                        })
                      }
                    >
                      <ListItemIcon>
                        <SettingsOutlinedIcon
                          style={{ color: theme.palette.action.active }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Project Settings" />
                      {projectsExpanded ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <ListItem className="project-full">
                    <Collapse
                      in={projectsExpanded}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List>
                        {projectSettingPages.map((value, index) => {
                          if (!!value.displayCheck && !value.displayCheck())
                            return undefined;

                          // Hide admin-only items if user is not admin
                          if (value.isAdminOnly && !isAdmin) return undefined;

                          return (
                            <RequireAuth
                              withRole={value.isAdminOnly ? "ADMIN" : ""}
                              key={index}
                            >
                              <ListItem
                                className="project-full"
                                key={index}
                                sx={{ paddingTop: 0, paddingBottom: 0 }}
                              >
                                <ListItemButton
                                  className="left-align-ListItem"
                                  onClick={() => goSettings(value.page, false)}
                                >
                                  <ListItemIcon />
                                  <ListItemText
                                    primary={value.primary}
                                    secondary={value.secondary}
                                  />
                                </ListItemButton>
                              </ListItem>
                            </RequireAuth>
                          );
                        })}
                      </List>
                    </Collapse>
                  </ListItem>
                </>
              )}
              <RequireAuth withRole="ADMIN">
                <ListItem className="project-full">
                  <ListItemButton
                    className="left-align-ListItem"
                    onClick={() =>
                      navBarDispatch({
                        type: globalExpanded ? "closeGlobal" : "openGlobal",
                      })
                    }
                  >
                    <ListItemIcon>
                      <LanguageIconOutlined />
                    </ListItemIcon>
                    <ListItemText primary="Global Settings" />
                    {globalExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <ListItem className="project-full">
                  <Collapse in={globalExpanded} timeout="auto" unmountOnExit>
                    <List>
                      {globalSettingPages.map<React.ReactNode>(
                        (value, index) => {
                          return (
                            <ListItem
                              className="project-full"
                              key={index}
                              sx={{ paddingTop: 0, paddingBottom: 0 }}
                            >
                              <ListItemButton
                                className="left-align-ListItem"
                                onClick={() => goSettings(value.page, true)}
                              >
                                <ListItemIcon />
                                <ListItemText
                                  primary={value.primary}
                                  secondary={value.secondary}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        },
                      )}
                    </List>
                  </Collapse>
                </ListItem>
              </RequireAuth>
            </List>
            <List className="general-drawer-footer-box">
              {showCompanySwitcher && company?.name && (
                <ListItemButton
                  className="button"
                  onClick={() => navigate("/company")}
                >
                  <ListItemIcon>
                    <DomainOutlinedIcon
                      style={{ color: theme.palette.action.active }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={company.name}
                    secondary={"Switch Company"}
                    slotProps={{
                      secondary: { sx: { textDecoration: "underline" } },
                    }}
                  />
                </ListItemButton>
              )}
              <ListItemButton className="button" onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon style={{ color: theme.palette.action.active }} />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
              <ListItemButton
                className="button"
                onClick={() => navBarDispatch({ type: "close" })}
              >
                <ListItemIcon style={{ color: theme.palette.action.active }}>
                  <FirstPageOutlinedIcon />
                </ListItemIcon>
                <ListItemText primary="Close Navigation" />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>
      </Paper>
      <Divider flexItem sx={{ opacity: 0.6 }} orientation="vertical" />
    </>
  );
}
